import { AsyncPipe } from '@angular/common';
import {
	ChangeDetectionStrategy,
	Component,
	computed,
	effect,
	inject,
	input,
	OnInit,
} from '@angular/core';
import { TranslocoPipe } from '@jsverse/transloco';
import {
	CentsToDollarsPipe,
	DestroyService,
	InfiniteScrollBaseComponent,
	PercentToValuePipe,
} from '@marmon/frontend/common';
import {
	ExportApiService,
	ExportButtonComponent,
} from '@marmon/frontend/export';
import {
	AnalysisCalculation,
	InvoiceRollup,
	RollupEntity,
} from '@marmon/shared/types/analysis';
import { QueryConstraintMap } from '@marmon/shared/types/common';
import { PageSize, SortDirMap, SortMod } from '@marmon/shared/types/pagination';
import { TuiTable } from '@taiga-ui/addon-table';
import { TuiLoader } from '@taiga-ui/core';
import { TuiSkeleton } from '@taiga-ui/kit';
import { where } from 'firebase/firestore';
import { map, of, switchMap, takeUntil, tap } from 'rxjs';
import { IsCostTypePipe } from '../../pipes';
import { AnalysisApiService, AnalysisStateService } from '../../services';

type AdditionalOptions = {
	calculation: AnalysisCalculation | null;
	queryConstraints: QueryConstraintMap[];
} | null;

@Component({
	selector: 'mar-entity-breakdown-table',
	templateUrl: 'entity-breakdown-table.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: true,
	providers: [DestroyService],
	imports: [
		TuiTable,
		TranslocoPipe,
		AsyncPipe,
		CentsToDollarsPipe,
		TuiSkeleton,
		PercentToValuePipe,
		TuiLoader,
		IsCostTypePipe,
		ExportButtonComponent,
	],
	host: { class: 'flex flex-col w-full min-w-0 h-3xl' },
})
export class EntityBreakdownTableComponent
	extends InfiniteScrollBaseComponent<InvoiceRollup, AdditionalOptions>
	implements OnInit
{
	readonly entity = input.required<RollupEntity>();
	readonly translation = computed(() => `customer-product.${this.entity()}`);

	readonly queryConstraints = input.required<QueryConstraintMap[]>();
	readonly allowExport = input<boolean>(true);

	override columnKeys: string[] = [];

	readonly analysisApiService = inject(AnalysisApiService);
	readonly analysisStateService = inject(AnalysisStateService);
	readonly exportApiService = inject(ExportApiService);

	readonly calculation$ = this.analysisStateService.calculation$
		.pipe(
			takeUntil(this.destroy$),
			map((value) => {
				if (!value) return null;
				return value;
			}),
			tap((calculation) => {
				this.additional$.next({
					queryConstraints: [],
					...(this.additional$.value ?? {}),
					calculation,
				});
			}),
		)
		.subscribe();

	override request$ = (
		options: [
			PageSize,
			keyof InvoiceRollup | null,
			SortMod,
			AdditionalOptions,
			void,
		],
	) =>
		of(options).pipe(
			switchMap(([size, , sortMod, additional]) => {
				const analysis = this.analysisStateService.analysis;
				if (!analysis) throw Error('No analysis found');

				const { calculation, queryConstraints } = additional ?? {};
				if (!calculation) throw Error('No calculation found');

				const constraints =
					queryConstraints?.map((q) =>
						where(q.property as string, q.op, q.value),
					) ?? undefined;

				return this.analysisApiService.fetch(
					this.paginatedObject.update({
						sortBy: null,
						sortDir: SortDirMap.get(sortMod),
						pageSize: size,
						constraints,
					}),
					{
						subcollections: [
							{ docId: analysis.id, name: 'calculations' },
							{
								docId: calculation.id,
								name: `${this.entity()}s`,
							},
						],
						next: this.loadingMore,
					},
				);
			}),
			map(() => this.paginatedObject),
		);

	constructor() {
		super();
		effect(() => {
			this.additional$.next({
				calculation: null,
				...this.additional$.value,
				queryConstraints: this.queryConstraints(),
			});
		});
	}

	ngOnInit() {
		this.columnKeys = determineColumnKeys(this.entity());
	}

	onExport() {
		const analysis = this.analysisStateService.analysis;
		const calculation = this.analysisStateService.calculation;
		if (!analysis || !calculation)
			throw Error('Export could not be generated');

		this.exportApiService.generate({
			analysisId: analysis.id,
			calculationId: calculation.id,
			type: `${this.entity()}sBreakdown`,
			constraints: this.queryConstraints(),
		});
	}
}

/**
 * Determines what the column keys should be based on the options provided
 *
 * @param entity the entity to generate the keys for
 */
const determineColumnKeys = (entity: RollupEntity) => [
	entity,
	'revenueInCents',
	'count80',
	'count20',
	'percent80',
	'transactionCount',
	'transactionAvgInCents',
	'marginPercent80',
	'marginPercent20',
	'marginPercent',
];
